<template>
  <div class="w-full flex items-center justify-center bg-white px-5 sm:px-10 py-8 sm:py-12 sm:pb-10">
    <div class="w-full max-w-md flex flex-col items-center text-slate-900">

      <!-- ====================== -->
      <!-- Initial Form State -->
      <!-- ====================== -->
      <template v-if="!success">
        <h1 class="text-2xl font-bold mb-2 text-center">
          Lost your tickets?
        </h1>

        <p class="text-sm text-slate-600 mb-6 text-center">
          Enter the email you used at checkout and we’ll resend your tickets.
        </p>

        <!-- Method Toggle -->
        <!-- <div class="flex gap-3 mb-6 w-full justify-center">
          <button
            type="button"
            @click="switchMethod('email')"
            :class="btnClass(method === 'email')"
          >
            Email
          </button>

          <button
            type="button"
            @click="switchMethod('phone')"
            :class="btnClass(method === 'phone')"
          >
            Phone
          </button>
        </div> -->

        <!-- Input -->
        <div class="w-full mb-4">

          <!-- Email -->
          <input
            v-if="method === 'email'"
            v-model.trim="email"
            type="email"
            class="w-full border rounded px-3 py-2 text-sm bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-black"
            placeholder="you@example.com"
          />

          <!-- Phone -->
          <div v-else>
            <label class="block text-xs text-slate-600 mb-1">Phone number *</label>

            <VueTelInput
              v-model="phoneDisplay"
              :defaultCountry="phoneCountryIso"
              @validate="onPhoneValidate"
              @country-changed="onCountryChanged"
              @blur="onPhoneBlur"
              :inputOptions="{
                inputClass:
                  'w-full rounded border px-3 py-2 outline-none ' +
                  (phoneError ? 'border-red-500 field-error' : 'border-slate-300 focus:border-black')
              }"
            />

          </div>
        </div>

        <!-- Error message -->
        <p v-if="errorMessage" class="text-xs text-red-600 mb-4 text-center">
          {{ errorMessage }}
        </p>

        <!-- Submit -->
        <button
          type="button"
          class="btn-ticketing-red w-full text-center"
          :disabled="submitting"
          @click="submit"
        >
          <span v-if="!submitting">Send my tickets</span>
          <span v-else>Sending…</span>
        </button>
      </template>

      <!-- ====================== -->
      <!-- Success -->
      <!-- ====================== -->
      <template v-else>
        <h1 class="text-2xl font-bold mb-4 text-center">
          Your tickets are on their way
        </h1>

        <p class="text-sm text-slate-700 mb-4 text-center">
          Your tickets have been sent to
          <span class="font-semibold">{{ displayTarget }}</span>.
        </p>

        <p class="text-sm text-slate-600 text-center mb-4">
          Make sure you also check your spam or junk folder. If you haven’t received them,
          contact us at
        </p>
        <div>
          <a href="mailto:info@summerjam.com.au" class="mt-2 font-semibold underline">
            info@summerjam.com.au
          </a>.          
        </div>
      </template>

    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { VueTelInput } from 'vue-tel-input'
import 'vue-tel-input/vue-tel-input.css'
import { usePublicTicketingApi } from '@/composables/api/ticketing/usePublicTicketingApi'

/* ---------------------------
   API
--------------------------- */
const { requestLostTickets } = usePublicTicketingApi()

/* ---------------------------
   State
--------------------------- */
const method = ref<'email' | 'phone'>('email')
const email = ref('')

// vue-tel-input
const phoneDisplay     = ref('')
const fullPhoneObject  = ref<any | null>(null)
const phoneValid       = ref(false)
const phoneCountryIso  = ref<'AU' | string>('AU')
const phoneError       = ref<string | null>(null)
const phoneTouched     = ref(false)   // 👈 NEW

const success      = ref(false)
const submitting   = ref(false)
const errorMessage = ref<string | null>(null)

/* ---------------------------
   Display Target
--------------------------- */
const displayTarget = computed(() => {
  if (method.value === 'email') return email.value || 'your email address'
  return 'your email address';
})

/* ---------------------------
   UI Helpers
--------------------------- */
const btnClass = (active: boolean) =>
  active
    ? 'px-4 py-2 rounded-full bg-black text-white text-xs sm:text-sm border border-black'
    : 'px-4 py-2 rounded-full bg-white text-xs sm:text-sm border border-slate-300 text-slate-700 hover:border-black'

const switchMethod = (m: 'email' | 'phone') => {
  method.value = m
  errorMessage.value = null

  // reset phone field error/touched when switching tabs
  if (m === 'phone') {
    phoneError.value = null
    phoneTouched.value = false
  }
}

/* ---------------------------
   Phone Validation
--------------------------- */
function onPhoneValidate(evt: any) {
  phoneValid.value = !!evt.valid

  fullPhoneObject.value = {
    country: evt.country,
    country_code: evt.countryCode,
    country_calling_code: evt.countryCallingCode,
    formatted: evt.formatted,
    national_number: evt.nationalNumber,
    number: evt.number,
  }

  // ❗ Don't show error until user has interacted (blur or submit)
  if (!phoneTouched.value) {
    phoneError.value = null
    return
  }

  // After touched, keep error in sync
  if (!evt.valid && phoneDisplay.value.trim() !== '') {
    phoneError.value = 'Please enter a valid phone number.'
  } else {
    phoneError.value = null
  }
}

function onPhoneBlur() {
  phoneTouched.value = true

  // Re-run error state based on current validity/value
  if (!phoneValid.value && phoneDisplay.value.trim() !== '') {
    phoneError.value = 'Please enter a valid phone number.'
  } else {
    phoneError.value = null
  }
}

function onCountryChanged(newCountry: any) {
  if (newCountry?.iso2) {
    phoneCountryIso.value = newCountry.iso2.toUpperCase()
  }
}

/* ---------------------------
   Email Validation
--------------------------- */
const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

/* ---------------------------
   Submit
--------------------------- */
const submit = async () => {
  errorMessage.value = null

  if (method.value === 'email') {
    if (!isValidEmail(email.value.trim())) {
      errorMessage.value = 'Please enter a valid email address.'
      return
    }
  } else {
    // Mark as touched when trying to submit
    phoneTouched.value = true

    if (!phoneValid.value || !fullPhoneObject.value) {
      phoneError.value = 'Please enter a valid phone number.'
      errorMessage.value = 'Please enter a valid phone number.'
      return
    }
  }

  const payload: any = { method: method.value }
  if (method.value === 'email') payload.email = email.value.trim()
  else payload.full_phone = fullPhoneObject.value

  try {
    submitting.value = true
    await requestLostTickets(payload)
    success.value = true
    errorMessage.value = null
  } catch (err: any) {
    errorMessage.value =
      err?.response?.data?.message ||
      'We could not find your ticket, please try again.'
    success.value = false
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped lang="scss">
.btn-ticketing-red {
  width: 100%;
  margin: 0 auto;
  color: white;
  border-radius: 6px;
  padding: 10px 20px;
  background-color: var(--color-widget-primary);
  transition: 0.3s;
  &:hover {
    background-color: var(--color-widget-secondary);
  }
}
</style>